import React, { useState, useEffect } from 'react';
import { Crown, Award, Users, Gift, Copy, ExternalLink, TrendingUp, CheckCircle } from 'lucide-react';

const FoundersDashboard = () => {
  const [founderStatus, setFounderStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const PUBLIC_URL = process.env.REACT_APP_PUBLIC_URL || window.location.origin;

  useEffect(() => {
    fetchFounderStatus();
  }, []);

  const fetchFounderStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/member/founders/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.ok && data.is_founder) {
        setFounderStatus(data);
      }
    } catch (error) {
      console.error('Error fetching founder status:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTierColor = (tier) => {
    const colors = {
      gold: 'from-yellow-400 to-yellow-600',
      silver: 'from-gray-300 to-gray-500',
      bronze: 'from-orange-400 to-orange-600',
      starter: 'from-blue-400 to-blue-600'
    };
    return colors[tier] || 'from-gray-400 to-gray-600';
  };

  const getTierIcon = (tier) => {
    const icons = {
      gold: Crown,
      silver: Award,
      bronze: Gift,
      starter: Users
    };
    const Icon = icons[tier] || Users;
    return <Icon className="h-12 w-12" />;
  };

  const getTierName = (tier) => {
    return tier.charAt(0).toUpperCase() + tier.slice(1) + ' Founder';
  };

  const copyReferralLink = () => {
    if (founderStatus?.status?.referral_code) {
      const link = `${PUBLIC_URL}/founders-waitlist?ref=${founderStatus.status.referral_code}`;
      navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your founder status...</p>
        </div>
      </div>
    );
  }

  if (!founderStatus || !founderStatus.is_founder) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Not a Founder Yet</h2>
          <p className="text-gray-600 mb-6">
            You don't have founder status. Contact support if you believe this is an error.
          </p>
          <a
            href="/founders-waitlist"
            className="inline-block bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700"
          >
            Join Founders Waitlist
          </a>
        </div>
      </div>
    );
  }

  const { status, benefits } = founderStatus;
  const tier = status.founder_tier;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className={`bg-gradient-to-r ${getTierColor(tier)} rounded-2xl shadow-2xl p-8 md:p-12 text-white mb-8`}>
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-6 mb-6 md:mb-0">
              <div className="bg-white bg-opacity-20 rounded-full p-6">
                {getTierIcon(tier)}
              </div>
              <div>
                <h1 className="text-4xl font-bold mb-2">{getTierName(tier)}</h1>
                <p className="text-xl opacity-90">Founder #{status.founder_number}</p>
                <p className="text-sm opacity-75 mt-1">Member since {new Date(status.joined_at).toLocaleDateString()}</p>
              </div>
            </div>
            <div className="text-center md:text-right">
              <div className="text-5xl font-bold mb-2">{status.referral_count}</div>
              <div className="text-sm opacity-90">People Referred</div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Benefits Section */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-md p-6 mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <Gift className="h-6 w-6 mr-2 text-indigo-600" />
                Your Founder Benefits
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {benefits && benefits.map((benefit, idx) => (
                  <div
                    key={idx}
                    className="border-2 border-gray-200 rounded-lg p-4 hover:border-indigo-300 hover:shadow-sm transition-all"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="bg-indigo-100 rounded-lg p-2">
                        <CheckCircle className="h-5 w-5 text-indigo-600" />
                      </div>
                      {benefit.value && (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                          {benefit.value}
                        </span>
                      )}
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-1">{benefit.name}</h3>
                    <p className="text-sm text-gray-600">{benefit.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Referral Stats */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <TrendingUp className="h-6 w-6 mr-2 text-green-600" />
                Referral Impact
              </h2>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg">
                  <div className="text-3xl font-bold text-green-600">{status.referral_count}</div>
                  <div className="text-sm text-gray-600 mt-1">Total Referrals</div>
                </div>
                <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg">
                  <div className="text-3xl font-bold text-blue-600">{status.referral_count * 2}</div>
                  <div className="text-sm text-gray-600 mt-1">Points Earned</div>
                </div>
                <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-violet-50 rounded-lg">
                  <div className="text-3xl font-bold text-purple-600">🏆</div>
                  <div className="text-sm text-gray-600 mt-1">Influencer</div>
                </div>
              </div>
            </div>
          </div>

          {/* Referral Card */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl shadow-2xl p-6 text-white sticky top-8">
              <h3 className="text-2xl font-bold mb-4">Share & Earn</h3>
              <p className="mb-6 opacity-90">
                Invite friends to join as Founders and unlock bonus rewards!
              </p>
              
              <div className="bg-white bg-opacity-20 rounded-lg p-4 mb-4">
                <div className="text-xs opacity-75 mb-2">Your Referral Code</div>
                <div className="text-2xl font-bold font-mono tracking-wider">{status.referral_code}</div>
              </div>

              <button
                onClick={copyReferralLink}
                className="w-full bg-white text-indigo-600 px-6 py-3 rounded-lg hover:bg-gray-100 font-semibold flex items-center justify-center mb-4 transition-colors"
              >
                {copied ? (
                  <>
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-5 w-5 mr-2" />
                    Copy Referral Link
                  </>
                )}
              </button>

              <div className="border-t border-white border-opacity-20 pt-4 mt-4">
                <h4 className="font-semibold mb-3">Referral Rewards:</h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <span className="mr-2">🎁</span>
                    <span>Each referral earns you bonus XP</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">🏆</span>
                    <span>Top referrers get exclusive perks</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">⚡</span>
                    <span>Help build the community!</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Founders Community */}
        <div className="mt-8 bg-white rounded-xl shadow-md p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Founders Community</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <a
              href="/member/boot-camp-vault"
              className="border-2 border-gray-200 rounded-lg p-6 hover:border-indigo-300 hover:shadow-md transition-all group"
            >
              <div className="text-4xl mb-3">📚</div>
              <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-indigo-600">Exclusive Training</h3>
              <p className="text-sm text-gray-600">Access founder-only courses and resources</p>
            </a>
            <a
              href="#"
              className="border-2 border-gray-200 rounded-lg p-6 hover:border-indigo-300 hover:shadow-md transition-all group"
            >
              <div className="text-4xl mb-3">💬</div>
              <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-indigo-600">Private Forum</h3>
              <p className="text-sm text-gray-600">Connect with other founders</p>
            </a>
            <a
              href="#"
              className="border-2 border-gray-200 rounded-lg p-6 hover:border-indigo-300 hover:shadow-md transition-all group"
            >
              <div className="text-4xl mb-3">🎤</div>
              <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-indigo-600">Monthly Calls</h3>
              <p className="text-sm text-gray-600">Join exclusive founder meetings</p>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FoundersDashboard;
